import { Provider } from '@dataTypes/provider.types'
import { getFullAddress } from '@utils/address.util'
import {
  formatPhoneNumber,
  formatSSN,
  formatTaxId,
} from '@utils/numberFormatter.util'
import React from 'react'

type DetailsProps = {
  provider: Provider | null
}

const formatDateOfBirth = (date: string | undefined): string => {
  if (!date) return ''
  const parsed = new Date(date)
  if (isNaN(parsed.getTime())) return date
  const month = String(parsed.getMonth() + 1).padStart(2, '0')
  const day = String(parsed.getDate()).padStart(2, '0')
  const year = parsed.getFullYear()
  return `${month}-${day}-${year}`
}

const formatPhone = (value: string | undefined | null): string | undefined =>
  value ? formatPhoneNumber(value, false) : undefined

// Section layout shared with the patient side panel (sp-details-* styles)
const DetailSection: React.FC<{
  title: string
  isLast?: boolean
  children: React.ReactNode
}> = ({ title, isLast = false, children }) => (
  <section
    className={`sp-details-section${isLast ? ' sp-details-section--last' : ''}`}
  >
    <div className="sp-details-section-header">
      <span className="sp-details-section-title">{title}</span>
    </div>
    <div className="sp-details-section-body sp-details-fields">{children}</div>
  </section>
)

const DetailField: React.FC<{
  label: string
  value: string | undefined | null
  fallback?: string
}> = ({ label, value, fallback = 'N/A' }) => (
  <div className="sp-details-field">
    <span className="sp-details-field-label">{label}</span>
    <span className="sp-details-field-value">{value || fallback}</span>
  </div>
)

const DetailRow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="sp-details-grid-row">{children}</div>
)

const Details: React.FC<DetailsProps> = ({ provider }): React.ReactElement => {
  if (!provider) {
    return (
      <div className="provider-details sp-details">
        <span className="sp-details-field-value sp-details-field-value--muted">
          No Personal Information
        </span>
      </div>
    )
  }

  const isTin = provider.tax_id_type === 'TIN'
  const taxIdValue = isTin
    ? provider.tax_id_number
      ? formatTaxId(provider.tax_id_number)
      : undefined
    : provider.social_security_number
      ? formatSSN(provider.social_security_number)
      : undefined

  return (
    <div className="provider-details sp-details">
      <DetailSection title="Contact information">
        <DetailRow>
          <DetailField label="Email" value={provider.email} />
        </DetailRow>
        <DetailRow>
          <DetailField label="Address" value={getFullAddress(provider)} />
        </DetailRow>
        <DetailRow>
          <DetailField
            label="Cellphone Number"
            value={formatPhone(provider.cellphone)}
          />
          <DetailField
            label="Home Number"
            value={formatPhone(provider.home_number)}
          />
        </DetailRow>
        <DetailRow>
          <DetailField
            label="Work Number"
            value={formatPhone(provider.work_contact)}
          />
        </DetailRow>
      </DetailSection>

      <DetailSection title="Personal details">
        <DetailRow>
          <DetailField
            label="Birthday"
            value={formatDateOfBirth(provider.date_of_birth)}
          />
          <DetailField label="Gender" value={provider.gender} />
        </DetailRow>
        <DetailRow>
          <DetailField label="Preferred Pronouns" value={provider.pronouns} />
        </DetailRow>
      </DetailSection>

      <DetailSection title="Professional details">
        <DetailRow>
          <DetailField
            label="Assigned Clinic"
            value={provider.clinic?.clinic_name}
          />
          <DetailField label="Specialty" value={provider.work_role} />
        </DetailRow>
        <DetailRow>
          <DetailField
            label="State License Number"
            value={provider.state_license_number}
          />
        </DetailRow>
        <DetailRow>
          <DetailField
            label="Expiry Date"
            value={provider.state_license_expiry}
          />
          <DetailField
            label="State"
            value={provider.state_where_licensed_issued}
          />
        </DetailRow>
        <DetailRow>
          <DetailField label="State Rx ID" value={provider.state_rx_id} />
          <DetailField label="DEA Number" value={provider.dea_number} />
        </DetailRow>
      </DetailSection>

      <DetailSection title="Tax and NPI" isLast>
        <DetailRow>
          <DetailField label="Tax ID Type" value={provider.tax_id_type} />
          <DetailField label={isTin ? 'TIN' : 'SSN'} value={taxIdValue} />
        </DetailRow>
        <DetailRow>
          <DetailField label="NPI Number" value={provider.npi_number} />
          <DetailField label="NPI Type" value={provider.npi_type} />
        </DetailRow>
      </DetailSection>
    </div>
  )
}

export default Details
